import React from 'react';

type BadgeVariant =
  | 'pending'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'declined'
  | 'disbursed'
  | 'active'
  | 'paid'
  | 'overdue'
  | 'defaulted'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';

interface StatusBadgeProps {
  status: string;
  variant?: BadgeVariant;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant,
  size = 'md',
  className = '',
}) => {
  // Auto-detect variant from status if not provided
  const getVariant = (): BadgeVariant => {
    if (variant) return variant;

    const lowerStatus = status.toLowerCase();

    // Application statuses
    if (lowerStatus === 'pending' || lowerStatus === 'draft') return 'pending';
    if (lowerStatus === 'submitted') return 'submitted';
    if (lowerStatus === 'under_review' || lowerStatus === 'under review') return 'under_review';
    if (lowerStatus === 'approved') return 'approved';
    if (lowerStatus === 'declined' || lowerStatus === 'rejected') return 'declined';
    if (lowerStatus === 'disbursed') return 'disbursed';

    // Loan statuses
    if (lowerStatus === 'active') return 'active';
    if (lowerStatus === 'paid' || lowerStatus === 'completed' || lowerStatus === 'settled') return 'paid';
    if (lowerStatus === 'overdue') return 'overdue';
    if (lowerStatus === 'defaulted' || lowerStatus === 'default') return 'defaulted';

    // Payment statuses
    if (lowerStatus === 'verified' || lowerStatus === 'confirmed') return 'success';
    if (lowerStatus === 'processing') return 'info';
    if (lowerStatus === 'failed') return 'error';

    return 'neutral';
  };

  const badgeVariant = getVariant();

  // Format status text for display
  const formatStatus = (str: string): string => {
    return str
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <span
      className={`status-badge status-badge-${badgeVariant} status-badge-${size} ${className}`}
      role="status"
      aria-label={`Status: ${formatStatus(status)}`}
    >
      {formatStatus(status)}
    </span>
  );
};
